// Module: haptics.js (components/ds)
// Purpose: Tiny guarded vibration helper for phone-tier touch feedback. This is
//   PROGRESSIVE ENHANCEMENT only — navigator.vibrate is Android Chrome; iOS
//   Safari ignores the Vibration API entirely (by design), which is fine: the
//   UI never depends on it. Every call no-ops when the API is unavailable, OR
//   the user prefers reduced motion, OR the user has turned haptics off via
//   the "ds-haptics" localStorage preference (default-on; see hapticsEnabled /
//   setHapticsEnabled below — surfaced as a toggle in MoreSheet).
//
// Usage:
//   import { haptics, hapticsEnabled, setHapticsEnabled } from "components/ds";
//   haptics.tap();      // ~10ms  — row open, tab change, drag-dismiss completion
//   haptics.success();  // pulse pattern — a positive / completed action
//   haptics.warn();     // heavier pattern — a caution / destructive path
//   hapticsEnabled();       // → boolean, current user preference (default true)
//   setHapticsEnabled(false); // persist the preference

const STORAGE_KEY = "ds-haptics";

/** @returns {boolean} the user's stored haptics preference. Absent key = enabled. */
export function hapticsEnabled() {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") return true;
  try {
    return window.localStorage.getItem(STORAGE_KEY) !== "false";
  } catch {
    /* storage unavailable (e.g. private mode) → default to enabled */
    return true;
  }
}

/** Persist the user's haptics preference. */
export function setHapticsEnabled(on) {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, on ? "true" : "false");
  } catch {
    /* storage unavailable — preference just won't persist this session */
  }
}

/** @returns {boolean} true when a vibration should be suppressed. */
function muted() {
  if (typeof navigator === "undefined" || typeof navigator.vibrate !== "function") return true;
  if (!hapticsEnabled()) return true;
  if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
    try {
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return true;
    } catch {
      /* matchMedia unsupported for this query → don't suppress on this axis */
    }
  }
  return false;
}

/** Fire navigator.vibrate(pattern) unless muted. Never throws. */
function vib(pattern) {
  if (muted()) return;
  try {
    navigator.vibrate(pattern);
  } catch {
    /* some engines throw on odd patterns — swallow, it's non-essential */
  }
}

export const haptics = {
  /** ~10ms light confirm — row open, tab change, drag-dismiss completion. */
  tap: () => vib(10),
  /** Two-pulse — a positive / completed action. */
  success: () => vib([12, 60, 20]),
  /** Three-pulse heavier — a caution / destructive path. */
  warn: () => vib([30, 40, 30]),
};

export default haptics;
